import Cookies from "js-cookie";
import React, { ComponentClass, PureComponent, ReactNode } from "react";
import { connect } from "react-redux";
import { compose } from "recompose";
import sha256 from "sha256";

import environment from "../../../environment";
import i18n from "../../../i18n";
import client from "../../../lib/graphql/apollo";
import { IAppState } from "../../../modules/reducers";
import { mapTarget2SellProductToProduct } from "../../../services/mapperService";
import { ITarget2SellPageParams } from "../../../types/global";
import { IProduct } from "../../../types/product";
import GetMe from "../withMe/graphql/GetMe";

import { IT2SLoadedAction, IT2SLoadedPayload } from "./redux/action";
import { setT2SLoaded } from "./redux/action";
import { getT2SLoaded } from "./redux/selector";

export interface IWithT2sProducts {
  onT2SProductClicked: (product: IProduct, productIndex: number) => void;
  t2sProducts: IProduct[] | null;
}

export interface IMapStateToProps {
  t2sLoaded: boolean;
}

const mapStateToProps = (state: IAppState): IMapStateToProps => ({
  t2sLoaded: getT2SLoaded(state),
});

export interface IDispatchProps {
  setT2SLoaded: (payload: IT2SLoadedPayload) => IT2SLoadedAction;
}

const mapDispatchToProps: IDispatchProps = {
  setT2SLoaded,
};

interface IState {
  values: IProduct[] | null;
}

interface IProps {
  t2sLoaded: boolean;
  setT2SLoaded: (payload: IT2SLoadedPayload) => IT2SLoadedAction;
}

type IT2SHOCComponentClass = <P extends object>(
  Component: React.ComponentType<P>
) => ComponentClass<P & IProps, IState>;

const t2sProducts = (pageId: string): IT2SHOCComponentClass => <
  P extends object
>(
  Component: React.ComponentType<P>
): ComponentClass<P & IProps, IState> =>
  class WithT2sProducts extends PureComponent<P & IProps> {
    public intervalId: number | null = null;
    public state: IState = {
      values: null,
    };

    private mounted: boolean = false;

    public componentWillUnmount(): void {
      window.T2SRecoStarted = false;
      this.mounted = false;
    }

    public componentDidMount = async (): Promise<void> => {
      const t2sPageParams = window._t2sparams || ({} as ITarget2SellPageParams);
      t2sPageParams.cID = environment.T2S.CID;
      t2sPageParams.pID = pageId.split("-")[0];
      t2sPageParams.eN = "view";
      t2sPageParams.lang = i18n.language.toUpperCase();
      t2sPageParams.hasRankOption = true;
      this.mounted = true;

      // tslint:disable-next-line
      const getMeResponse: any = await client.query({
        fetchPolicy: "cache-only",
        query: GetMe,
      });

      if (getMeResponse && getMeResponse.data && getMeResponse.data.me) {
        t2sPageParams.uEM = sha256(
          getMeResponse.data.me.email.trim().toLowerCase()
        );
        t2sPageParams.uID = Cookies.get("t2s-userId");
      }

      window._t2sparams = t2sPageParams;

      const scriptT2S = document.getElementById("t2sScript");

      const init = (): void => {
        this.props.setT2SLoaded({ t2sLoaded: true });
        if (!window.T2SRecoStarted && window.T2S && window.T2S.reco) {
          window.T2SRecoStarted = true;
          window.T2S.reco();
        }
        if (!this.state.values) {
          this.setT2sValues();
        }
      };

      if (!scriptT2S) {
        const t2sScript = document.createElement("script");
        t2sScript.id = "t2sScript";
        t2sScript.type = "text/javascript";
        t2sScript.async = true;
        t2sScript.onload = init;
        t2sScript.src =
          ("https:" === document.location.protocol ? "https" : "http") +
          "://static.target2sell.com/t2s.min.js";

        document.body.append(t2sScript);
      } else {
        init();
      }
    };

    public componentDidUpdate(): void {
      const scriptT2S = document.getElementById("t2sScript");

      if (scriptT2S && this.props.t2sLoaded && !this.state.values) {
        this.setT2sValues();
      }
    }

    public render(): ReactNode {
      return (
        <>
          <div
            id={`${environment.T2S.CID}-${pageId}`}
            className="t2s-container"
          />
          <Component
            {...this.props}
            t2sProducts={this.state.values}
            onT2SProductClicked={this.sendT2Sevent}
          />
        </>
      );
    }

    public sendT2Sevent = (product: IProduct, productIndex: number): void => {
      const t2sPageParams = window._t2sparams;

      if (t2sPageParams != null) {
        const eventParams = {
          cID: t2sPageParams.cID,
          eN: t2sPageParams.eN,
          iID: product.id,
          lang: t2sPageParams.lang,
          pID: t2sPageParams.pID,
          po: String(productIndex),
          ru: product.url!,
          sp: `${t2sPageParams.cID}-${pageId}`,
        };
        window.T2S.event("click", eventParams);
      }
    };

    public setT2sValues = (): void => {
      let count: number = 0;
      let t2sContainer: HTMLElement | null;

      if (!this.intervalId && !this.state.values) {
        this.intervalId = setInterval(() => {
          if (count >= 10 && this.intervalId) {
            clearInterval(this.intervalId);
          }
          t2sContainer = document.getElementById(
            `${environment.T2S.CID}-${pageId}`
          );

          if (t2sContainer && !!t2sContainer.textContent) {
            try {
              if (this.intervalId) {
                clearInterval(this.intervalId);
              }
              const response: { values: [] } = JSON.parse(
                t2sContainer.textContent
              );
              if (this.mounted) {
                this.setState({
                  values: mapTarget2SellProductToProduct(response.values),
                });
              }
            } catch (e) {
              // tslint:disable-next-line: no-console
              console.error(e);
              if (this.mounted) {
                this.setState({ values: [] });
              }
            }
          }
          count++;
        }, 100);
      }
    };
  };

export default (pageId: string): IT2SHOCComponentClass =>
  compose(connect(mapStateToProps, mapDispatchToProps), t2sProducts(pageId));
